import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  setShowViewSharingMenu,
  submitShareViewInviteRequest,
} from "redux/SingleDataset/actions";
import {
  getDataset,
  getSharingMenuAssociatedView,
  getShowViewSharingMenu,
} from "redux/SingleDataset/selectors";
import styles from "./style.module.scss";
import { State } from "redux/store";
import { isValidEmail } from "helpers/utility/utilities";
import { Id, toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import { SavedView } from "types";
import { UserRow } from "views/components/SharingMenu/UserRow";
import { InviteInput } from "views/components/SharingMenu/InviteInput";
import { RightSlideMenu } from "views/components/SharingMenu/RightSlideMenu";
import VLAvatar from "views/uikit/VLAvatar";
import ShareIcon from "assets/icons/ShareIcon";

interface SharingMenuProps {
  showSharingMenu: boolean;
  dataset: any;
  view: SavedView | null;
}

const ViewSharingMenu = ({
  showSharingMenu,
  dataset,
  view,
}: SharingMenuProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [insertedEmail, setInsertedEmail] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDescription(event.target.value);
  };

  useEffect(() => {
    if (insertedEmail.length === 0) {
      setErrorMsg("");
    } else if (!isValidEmail(insertedEmail)) {
      setErrorMsg("Please enter a valid email.");
    } else {
      setErrorMsg("");
    }
  }, [insertedEmail]);

  useEffect(() => {
    if (showSharingMenu) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      setInsertedEmail("");
      setDescription("");
    };
    //eslint-disable-next-line
  }, [showSharingMenu]);

  const inviteBtnDisabled = useMemo(() => {
    return !insertedEmail || errorMsg !== "";
  }, [insertedEmail, errorMsg]);
  const inputDisabled = useMemo(() => {
    return errorMsg.length > 0;
  }, [errorMsg]);

  function handleOutsideClick(event: MouseEvent) {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      dispatch(
        setShowViewSharingMenu({
          showSharingMenu: false,
          associatedViewId: null,
        })
      );
    }
  }

  const handleCloseIconClick = (event: React.MouseEvent<HTMLDivElement>) => {
    dispatch(
      setShowViewSharingMenu({ showSharingMenu: false, associatedViewId: null })
    );
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInsertedEmail(value.trim());
  };

  const handleSendInvitation = async () => {
    if (inviteBtnDisabled || !view?.id || !dataset?.id) {
      return;
    }
    const response = await dispatch(
      submitShareViewInviteRequest(
        dataset?.id,
        view?.id,
        insertedEmail,
        description
      ) as any
    );
    if (response.status !== 200) {
      setErrorMsg("Couldn't invite user, please try again later.");
    } else {
      successNotification();
      setInsertedEmail("");
    }
  };

  const toastIdRef = useRef<any>(null);

  const showToast = (Element: ReactNode): Id => {
    return toast(Element, {
      closeButton: false,
      closeOnClick: false,
      position: "bottom-center",
    });
  };

  const successNotification = () => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="SUCCESS"
        title={`Your saved view has been successfully shared with ${insertedEmail}.`}
        onClose={() => toast.dismiss(toastIdRef.current)}
        description={""}
      />
    );
  };

  const renderOwnerRow = () => {
    const ownerUser = view?.createdBy;
    if (!ownerUser) {
      return <noscript />;
    }
    const userAvatar = (
      <div>
        <VLAvatar size="xsmall" userAvatar={ownerUser.avatar_uri} />
      </div>
    );
    return (
      <UserRow
        email={ownerUser.email}
        name={ownerUser.name}
        avatarIcon={userAvatar}
        subtitle="View Owner"
        classname={styles.datasetOwnerRow}
        dataTestId="owner-row"
      />
    );
  };

  return (
    <RightSlideMenu
      showRightSlideMenu={showSharingMenu}
      handleCloseIconClick={handleCloseIconClick}
      title="Share this View"
      icon={<ShareIcon />}
      containerRef={containerRef}
    >
      <div className={styles.sharingMenuBody}>
        <div className={styles.textareaContainer}>
          <textarea
            className={styles.sharingMenuDescription}
            name="sharing-menu-description"
            id="sharing-menu-description"
            value={description}
            onChange={handleDescriptionChange}
            maxLength={250}
            placeholder="Add a personal message to give context for this view"
            autoFocus
            data-testid="description-input"
          />
          <div className={styles.charCounter}>
            {description.length}/250 Characters
          </div>
        </div>
        <InviteInput
          inputStr={insertedEmail}
          onInputChange={handleInputChange}
          onSubmitInvite={handleSendInvitation}
          inviteBtnDisabled={inviteBtnDisabled}
          inputDisabled={inputDisabled}
        />
        {errorMsg.length > 0 && (
          <div className={styles.sharingMenuInputErrorMsg}>{errorMsg}</div>
        )}
        {renderOwnerRow()}
        <div className={styles.sharingMenuFooter}>
          Views are accessible to all collaborators of a specific dataset
        </div>
      </div>
    </RightSlideMenu>
  );
};

const mapStateToProps = (state: State) => {
  return {
    showSharingMenu: getShowViewSharingMenu(state),
    view: getSharingMenuAssociatedView(state),
    dataset: getDataset(state),
  };
};

export default connect(mapStateToProps)(ViewSharingMenu);
